import type { CorrespondenceOut } from "@/api";

export type DropOutcome =
  | { file: string; ok: true; entries: CorrespondenceOut[] }
  | { file: string; ok: false; status: number; detail: string; existingEntryId?: string | null };

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/**
 * One sentence for everything a drop did (design §8.2: „2 Mails abgelegt,
 * 1 als Datei (nicht lesbar)"): what landed first, then what was already
 * there, then what was turned away and why. A mail the reader could not
 * parse — or not within its five seconds (Plan B) — still landed, as a
 * file, and is said as such, not as a failure.
 */
export function dropSummary(outcomes: readonly DropOutcome[]): string {
  const entries = outcomes.flatMap((outcome) => (outcome.ok ? outcome.entries : []));
  const mails = entries.filter((entry) => entry.kind === "email_in" || entry.kind === "email_out").length;
  const unreadable = entries.filter((entry) => entry.kind === "file" && entry.parseNote !== null).length;
  const files = entries.filter((entry) => entry.kind === "file" && entry.parseNote === null).length;

  const stored: string[] = [];
  if (mails > 0) stored.push(`${count(mails, "Mail", "Mails")} abgelegt`);
  if (files > 0) stored.push(`${count(files, "Datei", "Dateien")} abgelegt`);
  if (unreadable > 0) stored.push(`${unreadable} als Datei (nicht lesbar)`);

  const sentences: string[] = [];
  if (stored.length > 0) sentences.push(`${stored.join(", ")}.`);
  const duplicates = outcomes.filter((outcome) => !outcome.ok && outcome.status === 409).map((outcome) => outcome.file);
  if (duplicates.length > 0) sentences.push(`Schon in der Akte: ${duplicates.join(", ")}.`);
  for (const outcome of outcomes) {
    if (!outcome.ok && outcome.status !== 409) {
      sentences.push(sentence(`Nicht abgelegt: ${outcome.file} — ${outcome.detail}`));
    }
  }
  return sentences.join(" ");
}
